import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";

export default function About() {
  return (
    <MarketingLayout>
      <SEO
        title="About"
        description="The story behind Nestled: a small team building a cozy, privacy-first home-inventory app that helps real households remember where every little thing lives."
        path="/about"
      />
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Our story</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight">
          Built in a garage. For your garage.
        </h1>
        <p className="mt-5 text-stoneink-700 text-lg leading-relaxed">
          Nestled started after one too many Saturdays spent unstacking holiday bins to find a single warm-white string of lights. We didn&apos;t want a spreadsheet. We didn&apos;t want a warehouse-grade inventory system. We wanted something that <em>felt</em> like home — soft, warm, and forgiving.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-12">
        <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="rounded-3xl overflow-hidden border border-sand-300 bg-sand-200 aspect-[16/10]">
          <img src="https://images.unsplash.com/photo-1638780506095-3d61a4f0edd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxvcmdhbml6ZWQlMjBzdG9yYWdlJTIwYm94ZXMlMjBzaGVsZnxlbnwwfHx8fDE3ODE3MjQxNzB8MA&ixlib=rb-4.1.0&q=85"
            alt="Organized storage" className="w-full h-full object-cover" />
        </motion.div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-20 space-y-10">
        <Block heading="The kitchen-table thesis">
          A calmer home is downstream of a calmer mind. If you know where things are, you have more brain left over for the people you live with. That&apos;s it. That&apos;s the whole product.
        </Block>
        <Block heading="What we believe">
          <ul className="space-y-3">
            <li><strong className="text-stoneink-900">Your data is yours.</strong> We never sell it, never train models on it. Photos are stored with owner-only signed access.</li>
            <li><strong className="text-stoneink-900">Calm beats clever.</strong> No gamification, no streak shame. Add things when you feel like it.</li>
            <li><strong className="text-stoneink-900">Real homes are messy.</strong> Hierarchies bend, items move, family helps. The app should bend with you.</li>
            <li><strong className="text-stoneink-900">Free should be useful.</strong> 25 items is enough to organize one room and decide if we&apos;re worth the upgrade.</li>
          </ul>
        </Block>
        <Block heading="Who we are">
          A tiny team that ships fast and writes its own code. We built Nestled because we needed it. If you have an idea or a complaint, <Link to="/contact" className="text-terracotta-600 hover:text-terracotta-700 font-medium underline-offset-4 hover:underline">tell us</Link> — we read every message.
        </Block>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <div className="rounded-3xl bg-terracotta-50 border border-terracotta-100 p-10 text-center">
          <Heart weight="fill" size={28} className="mx-auto text-terracotta-500" />
          <h2 className="mt-4 font-heading text-3xl text-stoneink-900 font-semibold tracking-tight">Try it for your house.</h2>
          <p className="mt-2 text-stoneink-700">We&apos;ll be quietly happy if it helps.</p>
          <Link to="/register" className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium">
            Get started free <ArrowRight weight="bold" size={16} />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

const Block = ({ heading, children }) => (
  <div>
    <h2 className="font-heading text-2xl sm:text-3xl text-stoneink-900 font-medium tracking-tight">{heading}</h2>
    <div className="mt-3 text-stoneink-700 leading-relaxed">{children}</div>
  </div>
);
